import React, { useState } from 'react';
import ReminderIcon from '@grnhse/seedling/lib/birch/icons/reminder';
import LocationIcon from '@grnhse/seedling/lib/birch/icons/location';
import VideoCallIcon from '@grnhse/seedling/lib/birch/icons/video_call';
import ChatIcon from '@grnhse/seedling/lib/birch/icons/chat';
import t from 'shared/utils/translation';
import styled from 'styled-components';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import {
  SubSectionHeader,
  BodyText,
} from '@grnhse/seedling/lib/azalea/typography';
import { Link } from '@grnhse/seedling/lib/birch/typography';
import { Body } from '@grnhse/bonsai/lib/components/typography';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import { TertiaryButton } from '@grnhse/bonsai/lib/components/buttons';

type Props = {
  jobTitle: string;
  interviewName: string;
  interviewLength: number;
  interviewLocation: string;
  interviewInvitation: string;
  useVideo: boolean;
  videoConferencingProvider: string | null | undefined;
  bonsaiEnabled: boolean;
};

const BonsaiInterviewDetails = ({
  jobTitle,
  interviewName,
  interviewLength,
  interviewLocation,
  interviewInvitation,
  useVideo,
  videoConferencingProvider,
}: Props) => {
  const [showDetails, toggleShowDetails] = useState(true);

  const detailsToggleLabel = showDetails
    ? t('self_schedule.candidate_view.hide_details')
    : t('self_schedule.candidate_view.show_details');

  return (
    <Wrapper>
      <Stack spacing={5}>
        <div>
          <Body>{jobTitle}</Body>
          <Body medium>{interviewName}</Body>
        </div>

        {showDetails && (
          <div>
            <DetailWrapper>
              <ReminderIcon color={colors.darkerGrey} />
              <Body>
                {interviewLength} {t('self_schedule.minutes')}
              </Body>
            </DetailWrapper>

            {interviewLocation && (
              <DetailWrapper>
                <LocationIcon />
                <Body>{interviewLocation}</Body>
              </DetailWrapper>
            )}

            {useVideo && (
              <DetailWrapper>
                <VideoCallIcon />
                <Body>
                  {t('self_schedule.candidate_view.video_link', {
                    provider: videoConferencingProvider,
                  })}
                </Body>
              </DetailWrapper>
            )}

            {interviewInvitation && (
              <DetailWrapper>
                <ChatIcon />
                <Body>
                  <BonsaiEmailText
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                      __html: interviewInvitation,
                    }}
                  ></BonsaiEmailText>
                </Body>
              </DetailWrapper>
            )}
          </div>
        )}
        <BonsaiMobileLinkWrapper>
          <TertiaryButton
            ariaLabel={detailsToggleLabel}
            onClick={() => toggleShowDetails(!showDetails)}
          >
            {detailsToggleLabel}
          </TertiaryButton>
        </BonsaiMobileLinkWrapper>
      </Stack>
    </Wrapper>
  );
};

export default function InterviewDetails(props: Props) {
  const [showDetails, toggleShowDetails] = useState(true);

  const {
    jobTitle,
    interviewName,
    interviewInvitation,
    interviewLength,
    interviewLocation,
    useVideo,
    videoConferencingProvider,
  } = props;

  if (props.bonsaiEnabled) {
    return <BonsaiInterviewDetails {...props} />;
  }

  return (
    <Wrapper>
      <JobTitle>{jobTitle}</JobTitle>
      <InterviewName>{interviewName}</InterviewName>

      {showDetails && (
        <>
          <DetailWrapper>
            <ReminderIcon color={colors.darkerGrey} />
            <DetailsText>
              {interviewLength} {t('self_schedule.minutes')}
            </DetailsText>
          </DetailWrapper>

          {interviewLocation && (
            <DetailWrapper>
              <LocationIcon />
              <DetailsText>{interviewLocation}</DetailsText>
            </DetailWrapper>
          )}

          {useVideo && (
            <DetailWrapper>
              <VideoCallIcon />
              <DetailsText>
                {t('self_schedule.candidate_view.video_link', {
                  provider: videoConferencingProvider,
                })}
              </DetailsText>
            </DetailWrapper>
          )}

          {interviewInvitation && (
            <DetailWrapper>
              <ChatIcon />
              <EmailText
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{
                  __html: interviewInvitation,
                }}
              />
            </DetailWrapper>
          )}
        </>
      )}
      <MobileLink onClick={() => toggleShowDetails(!showDetails)}>
        {showDetails
          ? t('self_schedule.candidate_view.hide_details')
          : t('self_schedule.candidate_view.show_details')}
      </MobileLink>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex: 0 0 400px;
  border-right: 1px solid ${colors.lightGrey};
  padding-right: 30px;

  @media only screen and (max-width: 1024px) {
    padding: 0 60px;
    margin-bottom: 32px;
    border-right: none;
    flex: 1;
    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.05);
  }

  @media only screen and (max-width: 480px) {
    padding: 0 35px;
  }
`;

const JobTitle = styled(BodyText)`
  margin-bottom: 0;
`;

const InterviewName = styled(SubSectionHeader)`
  margin-top: 0;
  margin-bottom: 32px;

  @media only screen and (max-width: 1024px) {
    margin-bottom: 8px;
  }
`;

const DetailWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;

  & > svg {
    flex: 0 0 20px;
    margin-right: 15px;
  }

  & > span {
    flex: 1 1 auto;
    margin-right: 60px;

    p {
      margin-top: 0;
    }
  }
`;

const DetailsText = styled(BodyText)`
  margin: 0;
`;

const EmailText = styled(BodyText)`
  height: 500px;
  word-break: break-word;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.lightGrey};
    border-radius: 10px;
  }

  & > p:first-child {
    margin-top: 0;
  }

  img {
    max-width: 350px;
    height: auto;
  }

  @media only screen and (max-width: 1024px) {
    height: auto;
  }
`;

// This styled component exists to force fonts to be used in the Bonsai email text
const BonsaiEmailText = styled.p`
  height: 500px;
  word-break: break-word;
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0;

  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.lightGrey};
    border-radius: 10px;
  }

  & > p:first-child {
    margin-top: 0;
  }

  img {
    max-width: 350px;
    height: auto;
  }

  @media only screen and (max-width: 1024px) {
    height: auto;
  }
`;

const MobileLink = styled(Link)`
  display: none;

  @media only screen and (max-width: 1024px) {
    cursor: pointer;
    display: block;
    margin-bottom: 25px;
  }
`;

const BonsaiMobileLinkWrapper = styled.div`
  display: none;

  @media only screen and (max-width: 1024px) {
    cursor: pointer;
    display: block;
    margin-bottom: 25px;
    margin-top: -16px;
  }
`;
